// React & External Libraries
import { useCallback, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { saveAs } from "file-saver";

// Internal Components & Types
import Button from "../main/Button";
import Icon from "../main/Icon";
import { IEmployee } from "../../interfaces";
import { toRawBase64 } from "../../utils/imageSrc";
import { buildVcfAdr } from "../../utils/address";

// Styles
import "../styles/Actions.css";

// Types for better type safety
type ContactField = {
  type: string;
  value: string;
};

type ContactProcessor = (value: string) => string;

/**
 * Maskiert Fremdtext fuer einen vCard-Wert (RFC 6350 §3.4): Backslash zuerst,
 * dann Zeilenumbruch, Semikolon und Komma. Ohne das zerlegte ein `;` im
 * Firmennamen ORG in Organisations-Einheiten, ein Zeilenumbruch braeche die Zeile.
 */
const vcfText = (s: string): string =>
  s.replace(/\\/g, '\\\\').replace(/\r\n|\r|\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');

// vCard field generators - extracted for better organization
const vCardFields = {
  version: () => `\nVERSION:3.0`,
  name: (employee: IEmployee) => `\nN:${employee.lastName};${employee.firstName};`,
  formatedName: (name: string) => `\nFN:${name}`,
  // Ohne Firma, Marke und Standort keine leere ORG-Zeile.
  organization: (org: string) => (org ? `\nORG:${vcfText(org)}` : ''),
  // Funktion ist optional — ohne Wert keine leere TITLE-Zeile.
  title: (title: string) => (title ? `\nTITLE:${title}` : ''),
  workPhone: (phone: string) => `\nTEL;type=WORK;VOICE:${phone}`,
  cellPhone: (phone: string) => `\nTEL;type=CELL;MOBILE:${phone}`,
  address: (address: string) => `\nADR;type=WORK;type=pref:${address}`,
  geolocation: (lat: string, lng: string) => `\nGEO:geo:${lat}\\,${lng}`,
  photo: (photo: string) => `\nPHOTO;TYPE=JPEG;ENCODING=BASE64:${photo}`,
  email: (email: string) => `\nEMAIL:${email}`,
  timestamp: () => `\nREV:${new Date().toISOString()}`,
  url: (url: string) => `\nURL:${url}`,
  socialProfile: (type: string, value: string) => `\nX-SOCIALPROFILE;TYPE=${type}:${value}`,
  socialProfileNoLink: (type: string, value: string) => `\nX-SOCIALPROFILE;TYPE=${type};VALUE=TEXT:${value}`
};

// Contact type mapping - easily extensible for new platforms
const contactTypeMapping: Record<string, ContactProcessor> = {
  // Communication
  email: vCardFields.email,
  phone: vCardFields.workPhone,
  mobile: vCardFields.cellPhone,
  website: vCardFields.url,
  
  // Social Media - easy to extend
  whatsapp: (value) => vCardFields.socialProfileNoLink('whatsapp', value),
  facebook: (value) => vCardFields.socialProfileNoLink('facebook', value),
  instagram: (value) => vCardFields.socialProfile('instagram', value),
  linkedin: (value) => vCardFields.socialProfile('linkedin', value),
  provenexpert: (value) => vCardFields.socialProfile('provenexpert', value),
  twitter: (value) => vCardFields.socialProfile('twitter', value),
  tiktok: (value) => vCardFields.socialProfile('tiktok', value),
  youtube: (value) => vCardFields.socialProfile('youtube', value),

  // Messaging Apps - no clickable links for privacy/security
  telegram: (value) => vCardFields.socialProfileNoLink('telegram', value),
  signal: (value) => vCardFields.socialProfileNoLink('signal', value),
  discord: (value) => vCardFields.socialProfileNoLink('discord', value),
  
  // Professional Networks
  xing: (value) => vCardFields.socialProfile('xing', value),
  github: (value) => vCardFields.socialProfile('github', value),
};

// Utility function to add new contact types at runtime
const addContactType = (type: string, processor: ContactProcessor): void => {
  contactTypeMapping[type] = processor;
};

// Helper to add social media platforms easily
const addSocialMediaType = (type: string, socialType?: string): void => {
  const platformName = socialType || type;
  addContactType(type, (value) => vCardFields.socialProfile(platformName, value));
};

// Example usage for extending contact types:
// addSocialMediaType('snapchat');
// addSocialMediaType('threads'); 
// addContactType('skype', (value) => vCardFields.socialProfile('skype', value));
// addContactType('customField', (value) => `\nX-CUSTOM:${value}`);

function Actions({ employee }: { employee: IEmployee }) {
  const isMobile = useMediaQuery({ query: "(width <= 700px)" });

  // Extract contact fields logic
  const getContactFields = (employee: IEmployee): ContactField[] => {
    return [
      { type: 'email', value: employee.email },
      { type: 'phone', value: employee.phone },
      { type: 'mobile', value: employee.mobile },
      { type: 'whatsapp', value: employee.whatsapp },
      { type: 'website', value: employee.website },
      { type: 'facebook', value: employee.facebook },
      { type: 'instagram', value: employee.instagram },
      { type: 'provenexpert', value: employee.provenExpert }
    ].filter(contact => contact.value?.trim());
  };

  // Process dynamic contacts - fully flexible and extensible
  const processContactFields = (contactFields: ContactField[]): string => {
    return contactFields
      .map(({ type, value }) => {
        const processor = contactTypeMapping[type];
        return processor ? processor(value) : '';
      })
      .join('');
  };

  // Alles ausser dem Foto haengt nur an `employee` und bleibt synchron.
  const vCardRumpf = useMemo(() => {
    const { teams, brandKey } = employee;
    const addressString = buildVcfAdr(teams.address);
    const contactFields = getContactFields(employee);

    return {
      kopf:
        "BEGIN:VCARD" +
        vCardFields.version() +
        vCardFields.name(employee) +
        vCardFields.formatedName(`${employee.firstName} ${employee.lastName}`) +
        // Rueckfall (Spec Firmenname F1): Firma der Person → Marke → Hauptstandort.
        vCardFields.organization(employee.companyName || brandKey.brandName || teams?.name || '') +
        vCardFields.title(employee.role) +
        vCardFields.address(addressString) +
        vCardFields.geolocation(teams.address.latitude, teams.address.longtitude),
      rest:
        processContactFields(contactFields) +
        vCardFields.timestamp() +
        "\nEND:VCARD",
    };
  }, [employee]);

  const [laedt, setLaedt] = useState(false);

  /**
   * Das Foto wird ERST beim Klick beschafft.
   *
   * Seit der Bild-Auslagerung liefert der Kontrakt fuer `profileImage` eine URL
   * statt der Bytes. Das vCard-Format bettet das Foto aber als base64 ein —
   * stuende dort die URL, waere das Foto im Adressbuch weg, und zwar lautlos:
   * Die Datei laedt weiterhin, sie ist nur ohne Bild.
   *
   * Der Abruf ist praktisch immer ein Treffer im Browser-Cache, weil dasselbe
   * Bild unmittelbar davor schon als `<img>` geladen wurde. Scheitert er,
   * entsteht eine Kontaktdatei ohne Foto statt gar keiner — brauchbar ist
   * besser als korrekt und nutzlos.
   */
  const handleDownload = useCallback(async (): Promise<void> => {
    // Wiedereintritts-Sperre statt `disabled` am Button: Der Abruf dauert im
    // Regelfall nur den Cache-Zugriff, und `Button` kennt kein `disabled` —
    // eine gemeinsame Bestandskomponente dafuer zu erweitern waere ein
    // groesserer Eingriff als der Anlass rechtfertigt.
    if (laedt) return;
    setLaedt(true);
    try {
      const foto = await toRawBase64(employee.base64ProfileImage);
      const vCardData =
        vCardRumpf.kopf +
        (foto ? vCardFields.photo(foto) : "") +
        vCardRumpf.rest;
      const blob = new Blob([vCardData], { type: "text/vcard" });
      saveAs(blob, `${employee.urlKey}.vcf`);
    } finally {
      setLaedt(false);
    }
  }, [employee, vCardRumpf, laedt]);

  return (
    <div className="actions-container" style={{ gap: `${isMobile ? 10 : 15}px` }}>
      <Button
        buttonText="Save contact"
        rigthIcon={
          <Icon
            icon="download"
            color="white"
            size="19px"
          />
        }
        onClick={() => void handleDownload()}
        size="large"
      />
      <div id="od_widget_appointment" />
    </div>
  );
}

export default Actions;
